import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { apiKey } from "../../../config/config";
import { toast } from "react-toastify";
import { languageHelper } from "../../../config/helperFunctions";

function PaymentMethods() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const userOutletId = user?.user?.outlet_id?.toString() || "";
  const [selectedOutlet] = useState(userOutletId);
  const [outletName, setOutletName] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch outlet details
  useEffect(() => {
    if (!userOutletId) {
      setError("No outlet associated with this user.");
      return;
    }
    setLoading(true);
    axios
      .get(`${apiKey}/api/rms/admin/get-outlets/${userOutletId}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setOutletName(res.data[0].name);
        } else if (res.data && res.data.name) {
          setOutletName(res.data.name);
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load outlet details");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userOutletId, user?.token]);

  // Fetch payment methods for selected outlet
  const fetchPaymentMethods = useCallback(() => {
    if (!selectedOutlet) return;
    setLoading(true);
    axios
      .get(`${apiKey}/api/rms/admin/outlets/${selectedOutlet}/payment-methods`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        setPaymentMethods(res.data);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load payment methods");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedOutlet, user?.token]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const handleStatusChange = (index) => {
    const updated = [...paymentMethods];
    const isCurrentlyActive = updated[index].status === "active";
    
    updated[index].status = isCurrentlyActive ? "inactive" : "active";
    
    // If we turned it inactive and it was default, reset default
    if (isCurrentlyActive && updated[index].is_default) {
      updated[index].is_default = false;
    }
    
    setPaymentMethods(updated);
  };

  const handleDefaultChange = (index) => {
    const updated = paymentMethods.map((method, idx) => {
      if (idx === index) {
        return {
          ...method,
          is_default: true,
          status: "active", // Default method must be active
        };
      }
      return {
        ...method,
        is_default: false,
      };
    });
    setPaymentMethods(updated);
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedOutlet) {
      toast.warning("Please select an outlet first.");
      return;
    }

    setSaveLoading(true);
    const payload = {
      outlet_id: selectedOutlet,
      payment_methods: paymentMethods.map((pm) => ({
        payment_method_id: pm.payment_method_id,
        status: pm.status,
        is_default: pm.is_default,
      })),
    };

    axios
      .post(`${apiKey}/api/rms/admin/outlets/payment-methods`, payload, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        toast.success(res.data.message || "Payment methods updated successfully!");
        fetchPaymentMethods();
      })
      .catch((err) => {
        console.error(err);
        toast.error(err.response?.data?.message || "Failed to update payment methods");
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-6 border-0">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-d-bg-text text-2xl font-bold">
            {languageHelper("Payment Methods Management")}
          </h1>
        </div>

        {error && (
          <div className="flex items-center p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
            <span className="font-medium">{error}</span>
          </div>
        )}

        {outletName && (
          <div className="w-full mb-6 p-4 bg-gray-50 border border-gray-100 rounded-lg flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-500">{languageHelper("Outlet / Branch")}:</span>
            <span className="text-sm font-bold text-gray-800">{outletName}</span>
          </div>
        )}

        {selectedOutlet && (
          <form onSubmit={handleSubmit} className="w-full">
            <div className="overflow-x-auto w-full border border-gray-100 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-[#F8F9FC]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      {languageHelper("Payment Method")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      {languageHelper("Description")}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-32">
                      {languageHelper("Active Status")}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-32">
                      {languageHelper("Is Default")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paymentMethods.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                        {languageHelper("No payment methods found in the system.")}
                      </td>
                    </tr>
                  ) : (
                    paymentMethods.map((method, index) => (
                      <tr key={method.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">
                          {languageHelper(method?.payment_method?.name)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {method.description || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <label className="toggle-switch inline-block">
                            <input
                              type="checkbox"
                              checked={method.status === "active"}
                              onChange={() => handleStatusChange(index)}
                            />
                            <div className="toggle-switch-background">
                              <div className="toggle-switch-handle"></div>
                            </div>
                          </label>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <input
                            type="radio"
                            name="default_payment_method"
                            checked={method.is_default}
                            onChange={() => handleDefaultChange(index)}
                            className="w-4 h-4 text-d-cat-btn focus:ring-d-cat-btn border-gray-300"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                className="bg-d-cat-btn text-white text-sm font-semibold px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150 text-center w-fit relative"
                type="submit"
                disabled={saveLoading}
              >
                {saveLoading && (
                  <span className="absolute inset-0 w-full h-full flex items-center justify-center bg-black bg-opacity-30 rounded">
                    <img className="w-5" src="/assets/gif/button_loader.gif" alt="" />
                  </span>
                )}
                {languageHelper("Save Changes")}
              </button>
            </div>
          </form>
        )}
      </div>

      {loading && (
        <div className="absolute top-0 left-0 w-full h-full grid place-items-center bg-white bg-opacity-60 z-50">
          <img src="/assets/gif/dash2.gif" alt="Loading..." />
        </div>
      )}
    </main>
  );
}

export default PaymentMethods;
